import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock } from "lucide-react";
import { BlogPost } from "@/hooks/useBlogPosts";

interface RelatedArticlesProps {
  currentPost: BlogPost;
  allPosts: BlogPost[];
  onPostClick: (post: BlogPost) => void;
}

export const RelatedArticles = ({ currentPost, allPosts, onPostClick }: RelatedArticlesProps) => {
  // Find related articles based on category and tags
  const relatedPosts = allPosts
    .filter(post => post.id !== currentPost.id)
    .map(post => {
      let score = 0;
      
      // Same category gets highest score
      if (post.category === currentPost.category) score += 10;
      
      // Shared tags
      const sharedTags = post.tags.filter(tag => currentPost.tags.includes(tag));
      score += sharedTags.length * 5;
      
      return { post, score };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map(item => item.post);

  if (relatedPosts.length === 0) return null;

  return (
    <section className="mt-12 pt-8 border-t">
      <h2 className="text-2xl font-bold mb-6">Related Articles</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {relatedPosts.map((post) => (
          <Card 
            key={post.id} 
            className="hover:shadow-lg transition-shadow cursor-pointer group"
            onClick={() => onPostClick(post)}
          >
            <div className="aspect-video overflow-hidden rounded-t-lg">
              <img
                src={post.image}
                alt={post.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            </div>
            <CardHeader className="pb-3">
              <Badge variant="secondary" className="w-fit mb-2">
                {post.category}
              </Badge>
              <CardTitle className="text-lg line-clamp-2 group-hover:text-primary transition-colors">
                {post.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span>{post.author}</span>
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {post.readTime}
                </span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
};
